import type { DescriptionLang, SampleOrderType } from "@hisense/shared";
import type { FieldSpan } from "@/components/ui/FormLayout";
import type { SampleLineDraft } from "./SampleOrderLinesEditor";
import { vnDate } from "@hisense/shared";

/*
 * Bố cục trang Đơn hàng mẫu (19/09/2026, cùng chuẩn biểu mẫu sản phẩm và PI).
 *
 * Số liệu production 19/09/2026 — 302 đơn: loại đơn, ngày đặt, ngày cần 100%; người liên hệ 98%; nhà cung cấp 96%; điện thoại
 * 95%; bộ phận 94%; ghi chú 8%; đơn gửi khách 4%; email 1%; ngày gửi 1%; hãng vận chuyển, số vận đơn 0% (ba ô gửi hàng mẫu nên
 * gập, tự mở khi có dữ liệu). 457 dòng mẫu: mã, mô tả 100%; màu 96%; mẫu đối 96%; cỡ 94%; số yêu cầu 91%; loại 88%; số giữ lại
 * 87%; ảnh 71%; mã phụ 39%; ghi chú 38%; thương hiệu 22%; ghi chú mẫu đối 7%.
 *
 * Bảng này là nguồn của bố cục; test bắt buộc mọi trường phần đầu và mọi trường của dòng mẫu có đúng một chỗ.
 */

/** Phần đầu đơn mẫu (chuyển từ trang sửa sang đây để test đếm được đủ trường). */
export interface SampleHeaderDraft {
  orderType: SampleOrderType;
  supplierId: string;
  customerId: string;
  orderDate: string;
  deliveryDate: string;
  teamDepartment: string;
  labTestRequest: string;
  courier: string;
  trackingNo: string;
  shippedAt: string;
  printLanguage: DescriptionLang;
  remarks: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
}

export const initialSampleHeader = (): SampleHeaderDraft => ({
  orderType: "From Supplier",
  supplierId: "",
  customerId: "",
  orderDate: vnDate(),
  deliveryDate: "",
  teamDepartment: "",
  labTestRequest: "",
  courier: "",
  trackingNo: "",
  shippedAt: "",
  printLanguage: "en",
  remarks: "",
  contactPerson: "",
  contactPhone: "",
  contactEmail: "",
});

export type SampleGroupId = "options" | "partner" | "schedule" | "shipping" | "notes";

/** Trường phần đầu theo nhóm. Nhà cung cấp / khách hàng: một ô, đổi theo loại đơn. */
export const SAMPLE_HEADER_GROUPS: readonly { id: SampleGroupId; fields: readonly (keyof SampleHeaderDraft)[] }[] = [
  { id: "options", fields: ["printLanguage"] },
  { id: "partner", fields: ["orderType", "supplierId", "customerId", "contactPerson", "contactPhone", "contactEmail"] },
  { id: "schedule", fields: ["orderDate", "deliveryDate", "teamDepartment"] },
  { id: "shipping", fields: ["courier", "trackingNo", "shippedAt"] },
  { id: "notes", fields: ["remarks"] },
];

/**
 * Ẩn THEO QUYẾT ĐỊNH, không phải bỏ sót: "Yêu cầu kiểm nghiệm" — khách yêu cầu 13/09/2026 thôi nhập, bản in bỏ dòng đó. Cột và dữ
 * liệu cũ giữ nguyên (khách đổi ý thì chỉ cần hiện lại ô).
 */
export const SAMPLE_HEADER_HIDDEN_BY_DECISION = ["labTestRequest"] as const;

export type SampleLineFieldKey =
  | "image" | "productNo" | "subRef" | "type" | "brand" | "description" | "color" | "size"
  | "requestQuantity" | "retainedQuantity" | "controlSample" | "remarks";

/**
 * Ô của một dòng mẫu, theo hàng trên lưới 12 cột (ảnh đứng cạnh). Mẫu đối và ghi chú mẫu đối chung một ô. Ghi chú cả hàng, nhiều
 * dòng — khách nói sẽ nhập nhiều (quyết định trước 19/09/2026, giữ nguyên).
 */
export const SAMPLE_LINE_ROWS: readonly (readonly { key: SampleLineFieldKey; span: FieldSpan }[])[] = [
  [{ key: "productNo", span: 3 }, { key: "subRef", span: 3 }, { key: "type", span: 3 }, { key: "brand", span: 3 }],
  [{ key: "description", span: 6 }, { key: "color", span: 3 }, { key: "size", span: 3 }],
  [{ key: "requestQuantity", span: 3 }, { key: "retainedQuantity", span: 3 }, { key: "controlSample", span: 6 }],
  [{ key: "remarks", span: 12 }],
];
export const SAMPLE_LINE_ASIDE: SampleLineFieldKey = "image";

/** Trường của dòng mà mỗi ô mang. */
export const SAMPLE_LINE_FIELD_DATA: Record<SampleLineFieldKey, readonly (keyof SampleLineDraft)[]> = {
  image: ["imageUrl", "imageKey"], productNo: ["productNo"], subRef: ["subRef"], type: ["type"], brand: ["brand"],
  description: ["description", "descriptionZh"], color: ["color"], size: ["size"],
  requestQuantity: ["requestQuantity"], retainedQuantity: ["retainedQuantity"],
  controlSample: ["controlSampleUrl", "controlSampleKey", "controlSampleDescription"], remarks: ["remarks"],
};
/** Trường không có ô: khoá nháp, sản phẩm danh mục đã chọn, cờ dòng gõ tay (hiện thành nhãn "Thủ công"). */
export const SAMPLE_LINE_AUTOMATIC = ["key", "productId", "isManualEntry"] as const;

/** Số ô gửi hàng mẫu đã có dữ liệu — mục gập mở sẵn khi đơn có dữ liệu ở đó. */
export const sampleShippingFilled = (h: SampleHeaderDraft) =>
  (["courier", "trackingNo", "shippedAt"] as const).filter((k) => h[k].trim() !== "").length;
